<template>
  <div class="seyir-cell-wrapper">
    <span
      :id="iconId"
      v-b-tooltip.hover
      class="material-icons-outlined d-block"
      :class="[iconClass, { 'seyir-emphasized-icon': isDownloadedStatus }]"
      :title="tooltipText"
      @click="openDetailModal"
    >
      {{ iconName }}
    </span>

    <b-modal
      :id="modalId"
      :title="modalTitle"
      hide-footer
      centered
      size="lg"
    >
      <div class="mb-1">
        <div class="d-flex align-items-center mb-50">
          <span
            class="material-icons-outlined mr-50"
            :class="iconClass"
          >{{ iconName }}</span>
          <b>Durum:</b>&nbsp;{{ statusLabel }}
        </div>
        <div
          v-if="field && field.FailureReasonCategory"
          class="mb-50"
        >
          <b>Sebep:</b> {{ field.FailureReasonCategory }}
        </div>
        <div
          v-if="field && field.RecommendationMessage"
          class="bg-light-info rounded p-75 mb-50"
        >
          <b>Tavsiye:</b> {{ field.RecommendationMessage }}
        </div>
      </div>

      <div
        v-if="latestSuccessfulLogId"
        class="d-flex align-items-center justify-content-between bg-light-success rounded p-75 mb-75"
      >
        <div>
          <b>Bu gün için dosya başarıyla indirildi.</b>
          <div class="text-muted small">
            En son başarılı indirme dosyası kullanılacaktır.
            <span v-if="latestSuccessfulFileName">({{ latestSuccessfulFileName }})</span>
          </div>
        </div>
        <b-button
          size="sm"
          variant="success"
          :disabled="downloadingPrimary"
          @click="downloadLatestSuccessful"
        >
          <span v-if="downloadingPrimary">...</span>
          <span v-else>Dosyayı İndir</span>
        </b-button>
      </div>

      <div
        v-if="canRetrigger"
        class="d-flex align-items-center justify-content-between bg-light-primary rounded p-75 mb-75"
      >
        <div>
          <b>Bu sürücü için sorguyu yeniden çalıştır.</b>
          <div class="text-muted small">Seyir servisi her zaman en güncel mevcut dosyayı döndürür.</div>
        </div>
        <b-button
          size="sm"
          variant="primary"
          :disabled="retriggering"
          @click="retrigger"
        >
          <span v-if="retriggering">...</span>
          <span v-else>Yeniden Sorgula</span>
        </b-button>
      </div>

      <div
        v-if="detailsLoading"
        class="text-center py-1"
      >
        <b-spinner small variant="primary" />
        <span class="ml-50 text-muted">Detaylar yükleniyor...</span>
      </div>
      <div
        v-else-if="details.length === 0"
        class="text-muted"
      >
        Bu gün için detay bulunamadı.
      </div>
      <div v-else>
        <div
          v-for="(detail, index) in details"
          :key="`${modalId}-${index}`"
          class="border rounded p-75 mb-75"
        >
          <div><b>Dosya:</b> {{ detail.DownloadedFileName || "-" }}</div>
          <div><b>İndirme:</b> {{ detail.IsDownloadSuccessful ? "Başarılı" : "Başarısız" }}</div>
          <div><b>Takograf İşleme:</b> {{ detail.IsTachographProcessed ? "Başarılı" : "İşlenemedi" }}</div>
          <div><b>Tarih Aralığı:</b> {{ formatDateRange(detail.TachographDataStartDate, detail.TachographDataEndDate) }}</div>
          <div v-if="detail.LocalizedServiceMessage || detail.ServiceMessage">
            <b>Servis Mesajı:</b> {{ detail.LocalizedServiceMessage || detail.ServiceMessage }}
            <div v-if="detail.LocalizedServiceMessage && detail.ServiceMessage && detail.LocalizedServiceMessage !== detail.ServiceMessage">
              <small class="text-muted">Orijinal: {{ detail.ServiceMessage }}</small>
            </div>
          </div>
          <div v-if="detail.TachographProcessMessage">
            <b>Takograf Mesajı:</b> {{ detail.TachographProcessMessage }}
          </div>
          <div
            v-if="detail.LogId && detail.IsDownloadSuccessful"
            class="mt-50"
          >
            <b-button
              size="sm"
              variant="outline-primary"
              @click="downloadFile(detail)"
            >
              Dosyayı İndir
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BSpinner, VBTooltip } from 'bootstrap-vue'
import EmployeeOperationsApi from '@/api/employee-operations'

const STATUS_ICON_MAP = {
  NotWorking: { icon: 'remove_circle_outline', cls: 'text-muted', label: 'Çalışmıyor' },
  FutureDate: { icon: 'event', cls: 'text-muted', label: 'Gelecek Tarih' },
  Excused: { icon: 'event_busy', cls: 'text-info', label: 'İzinli' },
  NotConfigured: { icon: 'settings', cls: 'text-secondary', label: 'Yapılandırma Eksik' },
  NotQueried: { icon: 'schedule', cls: 'text-warning', label: 'Sorgu Yapılmadı' },
  QueryFailed: { icon: 'sync_problem', cls: 'text-danger', label: 'Sorgu Başarısız' },
  FileAlreadyDownloaded: { icon: 'task_alt', cls: 'text-primary', label: 'Dosya Zaten İndirilmiş' },
  DownloadFailedOther: { icon: 'cloud_off', cls: 'text-danger', label: 'Dosya İndirilemedi' },
  DownloadedNotProcessed: { icon: 'download_done', cls: 'text-warning', label: 'Takograf İşlenemedi' },
  Succeeded: { icon: 'check_circle', cls: 'text-success', label: 'Başarılı' },
}

export default {
  components: {
    BModal,
    BButton,
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    row: Object,
    field: Object,
  },
  data() {
    return {
      retriggering: false,
      downloadingPrimary: false,
      detailsLoading: false,
      detailsLoaded: false,
      lazyDetails: [],
    }
  },
  computed: {
    details() {
      if (this.detailsLoaded) return this.lazyDetails
      return this.field?.Details || []
    },
    hasAnySuccessfulDownload() {
      return !!this.field?.IsDownloaded
    },
    latestSuccessfulLogId() {
      return this.field?.LatestSuccessfulLogId || null
    },
    latestSuccessfulFileName() {
      return this.field?.LatestSuccessfulFileName || null
    },
    statusCode() {
      if (this.hasAnySuccessfulDownload) return 'Succeeded'
      if (this.field?.DayStatusCode) return this.field.DayStatusCode
      if (!this.field?.IsEmployeeWorking) return 'NotWorking'
      if (this.field?.IsExcused) return 'Excused'
      if (this.field?.IsEmployeeWorking && this.field?.IsDownloaded && this.field?.IsTachographProcessed) return 'Succeeded'
      if (this.field?.IsEmployeeWorking && this.field?.IsDownloaded) return 'DownloadedNotProcessed'
      if (this.field?.IsEmployeeWorking && this.field?.IsQueried) return 'DownloadFailedOther'
      if (this.field?.IsEmployeeWorking) return 'NotQueried'
      return 'NotWorking'
    },
    statusMeta() {
      return STATUS_ICON_MAP[this.statusCode] || STATUS_ICON_MAP.NotWorking
    },
    iconName() {
      return this.statusMeta.icon
    },
    iconClass() {
      return this.statusMeta.cls
    },
    isDownloadedStatus() {
      return ['Succeeded', 'DownloadedNotProcessed'].includes(this.statusCode)
    },
    statusLabel() {
      if (this.hasAnySuccessfulDownload) return this.statusMeta.label
      return this.field?.DayStatusLabel || this.statusMeta.label
    },
    iconId() {
      return `seyir-status-icon-${this.row?.EmployeeId || 'row'}-${(this.field?.DateStr || 'date').split('.').join('-')}`
    },
    modalId() {
      return `seyir-status-modal-${this.row?.EmployeeId || 'row'}-${(this.field?.DateStr || 'date').split('.').join('-')}`
    },
    modalTitle() {
      return `${this.row?.FullName || '-'} | ${this.field?.DateStr || '-'}`
    },
    canRetrigger() {
      if (!this.row?.EmployeeId) return false
      if (!this.field?.DateStr) return false
      const today = new Date()
      const todayStr = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`
      return this.field.DateStr === todayStr
    },
    tooltipText() {
      const parts = [this.statusLabel]
      if (!['NotWorking', 'Excused', 'FutureDate'].includes(this.statusCode)) {
        if (this.field?.RecommendationMessage) parts.push(this.field.RecommendationMessage)
        else if (this.field?.LocalizedServiceMessage) parts.push(this.field.LocalizedServiceMessage)
        else if (this.field?.ServiceMessage) parts.push(this.field.ServiceMessage)
      }
      return parts.filter(Boolean).join(' | ')
    },
  },
  methods: {
    openDetailModal() {
      if (['NotWorking', 'Excused', 'FutureDate'].includes(this.statusCode)) return
      this.$bvModal.show(this.modalId)
      this.loadDetailsLazy()
    },
    async loadDetailsLazy() {
      if (this.detailsLoaded || this.detailsLoading) return
      if (!this.row?.EmployeeId || !this.field?.DateStr) return
      const parts = this.field.DateStr.split('.')
      if (parts.length !== 3) return
      const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`
      try {
        this.detailsLoading = true
        const res = await EmployeeOperationsApi.Tachograph.SeyirDriverFileDownloadDayDetails(this.row.EmployeeId, isoDate)
        if (res?.data?.Success) {
          this.lazyDetails = res.data.Data || []
          this.detailsLoaded = true
        } else {
          this.$result(res)
        }
      } catch (err) {
        this.$result(null, err)
      } finally {
        this.detailsLoading = false
      }
    },
    async retrigger() {
      if (!this.row?.EmployeeId) return
      try {
        this.retriggering = true
        const res = await EmployeeOperationsApi.Tachograph.SeyirTriggerDriverFileDownload(this.row.EmployeeId)
        this.$result(res)
        if (res?.data?.Success) {
          this.$bvModal.hide(this.modalId)
          this.$root.$emit('seyir-driver-retriggered')
        }
      } catch (err) {
        this.$result(null, err)
      } finally {
        this.retriggering = false
      }
    },
    formatDateRange(startDate, endDate) {
      if (!startDate && !endDate) return '-'
      const start = startDate ? new Date(startDate).toLocaleDateString('tr-TR') : '-'
      const end = endDate ? new Date(endDate).toLocaleDateString('tr-TR') : '-'
      return `${start} - ${end}`
    },
    async downloadLatestSuccessful() {
      if (!this.latestSuccessfulLogId) return
      try {
        this.downloadingPrimary = true
        await this.downloadFile({
          LogId: this.latestSuccessfulLogId,
          DownloadedFileName: this.latestSuccessfulFileName,
        })
      } finally {
        this.downloadingPrimary = false
      }
    },
    async downloadFile(detail) {
      try {
        const response = await EmployeeOperationsApi.Tachograph.SeyirDriverFileDownload(detail.LogId)
        if ((response.headers['content-type'] || '').includes('application/json')) {
          return
        }
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' })
        const contentDisposition = response.headers['content-disposition'] || ''
        const fileNameFromHeader = this.resolveFileNameFromContentDisposition(contentDisposition)
        const fileName = fileNameFromHeader || detail.DownloadedFileName || 'seyir-takograf.dff'

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error(error)
      }
    },
    resolveFileNameFromContentDisposition(contentDisposition) {
      if (!contentDisposition) return null
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) return decodeURIComponent(utf8Match[1])
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
      if (fileNameMatch && fileNameMatch[1]) return fileNameMatch[1]
      return null
    },
  },
}
</script>

<style scoped>
.seyir-cell-wrapper {
    cursor: pointer;
}
.seyir-emphasized-icon {
    font-size: 1.6rem;
    font-weight: 700;
    -webkit-text-stroke: 0.6px currentColor;
    text-stroke: 0.6px currentColor;
}
</style>
